import { ShippingsFilter } from '../models';

export const SHIPPING_EXPORT_MAX_DATE_RANGE_DAYS = 45;

const MS_PER_DAY = 86400000;

/**
 * `dateToDelivery` (ETA) es una columna DATEONLY (día calendario). El backend la
 * compara formateando el valor con la zona horaria del server, así que un borde
 * en UTC (00:00Z) se corre al día anterior en un server UTC-3. Anclamos el día
 * calendario elegido al mediodía UTC para que no se corra ni al día anterior ni
 * al posterior, sea cual sea el offset del server.
 */
const toDeliveryDayParam = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T12:00:00.000Z`;
};

export function getShippingExportDateRangeValidationMessage(
  filter: ShippingsFilter
): string | null {
  // Buscando un pedido puntual por número el backend ignora las fechas, así
  // que exigir un rango acá solo bloquearía un export que sí va a funcionar.
  if (filter.number?.trim() && !Number.isNaN(Number(filter.number.trim()))) {
    return null;
  }

  const ranges: { from: Date; to: Date; label: string }[] = [];

  if (filter.deliveryDateFrom != null && filter.deliveryDateTo != null) {
    ranges.push({ from: filter.deliveryDateFrom, to: filter.deliveryDateTo, label: 'Fecha ETA' });
  }
  if (filter.dateDeliveredFrom != null && filter.dateDeliveredTo != null) {
    ranges.push({ from: filter.dateDeliveredFrom, to: filter.dateDeliveredTo, label: 'Fecha entregado' });
  }
  if (filter.createdAtFrom != null && filter.createdAtTo != null) {
    ranges.push({ from: filter.createdAtFrom, to: filter.createdAtTo, label: 'Fecha ingreso' });
  }

  if (ranges.length === 0) {
    return 'Para exportar, indicá al menos un rango de fechas.';
  }

  for (const range of ranges) {
    if (range.from.getTime() > range.to.getTime()) {
      return `Para exportar, en ${range.label} la fecha desde debe ser anterior o igual a la fecha hasta.`;
    }
    const spanMs = range.to.getTime() - range.from.getTime();
    if (spanMs > SHIPPING_EXPORT_MAX_DATE_RANGE_DAYS * MS_PER_DAY) {
      return `Para exportar, el rango de ${range.label} no puede superar ${SHIPPING_EXPORT_MAX_DATE_RANGE_DAYS} días.`;
    }
  }

  return null;
}

export function buildShippingExportRequestBody(
  filter: ShippingsFilter
): Record<string, string | number | string[]> {
  const body: Record<string, string | number | string[]> = {};

  if (filter.deliveryDateFrom != null && filter.deliveryDateTo != null) {
    body.deliveryDateFrom = toDeliveryDayParam(filter.deliveryDateFrom);
    body.deliveryDateTo = toDeliveryDayParam(filter.deliveryDateTo);
  }
  if (filter.dateDeliveredFrom != null && filter.dateDeliveredTo != null) {
    body.dateDeliveredFrom = filter.dateDeliveredFrom.toISOString();
    body.dateDeliveredTo = filter.dateDeliveredTo.toISOString();
  }
  if (filter.createdAtFrom != null && filter.createdAtTo != null) {
    body.dateFrom = filter.createdAtFrom.toISOString();
    body.dateTo = filter.createdAtTo.toISOString();
  }

  const numberTrimmed = filter.number?.trim();
  if (numberTrimmed) {
    const parsed = Number(numberTrimmed);
    if (!Number.isNaN(parsed)) {
      body.number = parsed;
    }
  }

  if (filter.statusId?.trim()) {
    body.statusId = filter.statusId.trim();
  }
  if (filter.commerceId?.trim()) {
    body.commerceId = filter.commerceId.trim();
  }
  if (filter.logisticOperatorId?.trim()) {
    body.logisticOperatorId = filter.logisticOperatorId.trim();
  }
  if (filter.recipient?.trim()) {
    body.recipient = filter.recipient.trim();
  }
  if (filter.recipientPhoneNumber?.trim()) {
    body.recipientPhoneNumber = filter.recipientPhoneNumber.trim();
  }
  if (filter.commerceShippingNumber?.trim()) {
    body.commerceShippingNumber = filter.commerceShippingNumber.trim();
  }
  if (filter.originAddress?.trim()) {
    body.originAddress = filter.originAddress.trim();
  }
  if (filter.destinationAddress?.trim()) {
    body.destinationAddress = filter.destinationAddress.trim();
  }
  if (filter.branchId?.trim()) {
    body.branchId = filter.branchId.trim();
  }
  if (filter.cityIds?.length) {
    body.cityIds = filter.cityIds;
  }
  if (filter.destinationStateOrDepartmentIds?.length) {
    body.destinationStateOrDepartmentIds = filter.destinationStateOrDepartmentIds;
  }
  if (filter.tags?.trim()) {
    body.tags = filter.tags.trim();
  }
  if (filter.labelNumber?.trim()) {
    body.labelNumber = filter.labelNumber.trim();
  }
  if (filter.shippingLevels?.length) {
    body.shippingLevels = filter.shippingLevels;
  }
  if (filter.timeSlotIds?.length) {
    body.timeSlotIds = filter.timeSlotIds;
  }
  if (filter.serviceWithOperatorIds?.length) {
    body.serviceWithOperatorIds = filter.serviceWithOperatorIds;
  }
  if (filter.zoneIds?.length) {
    body.zoneIds = filter.zoneIds;
  }
  if (filter.driverId?.trim()) {
    body.driverId = filter.driverId.trim();
  }

  return body;
}
